/**
 * Helpers for Okta authorization bootstrap and session handling.
 * These utilities coordinate login redirects, token inspection, and the
 * app's session-refresh timing.
 * @flow
 */

/** Default refresh interval used when token expiry is unavailable. */
export const DEFAULT_SESSION_REFRESH_MS = 60 * 60 * 1000;
/** Minimum refresh delay used when expiry math produces a non-positive value. */
export const MIN_SESSION_REFRESH_INTERVAL_MS = 30 * 1000;
/** Maximum refresh delay allowed from token expiry math. */
export const MAX_SESSION_REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1000;

/** URL query parameter keys read during app bootstrap. */
export const QUERY_PARAM_COLLAB_ID = 'collabid';
export const QUERY_PARAM_SAVED_SEARCH_ID = 'savedsearchid';

export const VALID_COLLAB_PATTERN = /^[A-Za-z0-9_-]{10}$/;
export const VALID_BOOKMARK_ID_PATTERN = /^[A-Za-z0-9_-]{36}$/;

const OKTA_TOKEN_STORAGE_KEY = 'okta-token-storage';

/**
 * Calculates the delay until the Okta access token expires.
 * Falls back to the default refresh interval when expiry is missing or invalid.
 */
export async function getOktaAccessTokenExpiresInMs(
  getAccessToken /*: () => Promise<{ +expiresAt?: number, ... }> */
) /*: Promise<number> */ {
  try {
    const token = await getAccessToken();
    const expiresAt = token?.expiresAt;
    if (typeof expiresAt !== 'number' || Number.isNaN(expiresAt)) {
      return DEFAULT_SESSION_REFRESH_MS;
    }
    const remainingMs = expiresAt * 1000 - Date.now();
    if (!Number.isFinite(remainingMs)) {
      return DEFAULT_SESSION_REFRESH_MS;
    }
    if (remainingMs <= 0) {
      return MIN_SESSION_REFRESH_INTERVAL_MS;
    }
    return Math.min(MAX_SESSION_REFRESH_INTERVAL_MS, remainingMs);
  } catch {
    return DEFAULT_SESSION_REFRESH_MS;
  }
}

export function waitMs(delayMs: number): Promise<void> {
  return new Promise((resolve: (value: void) => void) => {
    setTimeout(resolve, delayMs);
  });
}

export function getIdTokenEmailFromClaims(idToken: ?Object): ?string {
  return idToken?.claims?.email ?? null;
}

export function getEmailFromOktaTokenStorage() {
  try {
    const tokenStorage = localStorage.getItem(OKTA_TOKEN_STORAGE_KEY);
    if (!tokenStorage) {
      return null;
    }
    const parsedToken = JSON.parse(tokenStorage);
    return parsedToken?.idToken?.claims?.email ?? null;
  } catch (e) {
    console.log('Error reading from localStorage:', e);
    return null;
  }
}

/**
 * Resolves the user's email from the ID token, retrying storage when needed.
 * @param {Function} getIdToken - Async accessor for the current ID token.
 * @param {Object} idToken - Token snapshot from the current auth client.
 * @returns {Promise<?string>} Email address when available.
 */
export async function resolveEmailFromIdToken(
  getIdToken: () => Promise<Object>,
  idToken: ?Object
): Promise<?string> {
  const claimsEmail = getIdTokenEmailFromClaims(idToken);
  if (claimsEmail) {
    return claimsEmail;
  }
  if (!idToken?.idToken) {
    return null;
  }

  let emailValue = getEmailFromOktaTokenStorage();
  if (emailValue) {
    console.log('Got email from localStorage token storage');
    return emailValue;
  }

  await waitMs(500);
  const retryIdToken = await getIdToken();
  emailValue = getIdTokenEmailFromClaims(retryIdToken);
  if (emailValue) {
    return emailValue;
  }

  return getEmailFromOktaTokenStorage();
}

/** Logs a minimal ID-token shape for debugging authorization bootstrap. */
export function logIdTokenStructure(idToken: ?Object, tag: string = '[TWithAuthorization]'): void {
  console.log(`${tag} idToken structure:`, {
    hasToken: Boolean(idToken),
    hasValue: Boolean(idToken?.idToken),
    hasClaims: Boolean(idToken?.claims)
  });
}

/**
 * Persists supported query-string session ids into sessionStorage.
 * @param {string} pathName - Current pathname.
 * @param {string} collabId - Collaboration id from the query string.
 * @param {string} bookmarkId - Saved-search id from the query string.
 * @param {Function} validatePathNameFn - Path validation helper.
 */
export function persistQuerySessionIds(
  pathName: string,
  collabId: string,
  bookmarkId: string,
  validatePathNameFn: Function
): void {
  const hasCollabId = validatePathNameFn(pathName, collabId);
  const hasBookmarkId = validatePathNameFn(pathName, bookmarkId);

  if (hasCollabId && VALID_COLLAB_PATTERN.exec(collabId)) {
    sessionStorage.setItem('cid', collabId);
  } else if (hasBookmarkId && VALID_BOOKMARK_ID_PATTERN.exec(bookmarkId)) {
    sessionStorage.setItem('bid', bookmarkId);
  }
}

/**
 * Resolves whether the user is already logged in after auth initialization.
 * Retries briefly when a callback redirect has just been processed.
 */
export async function resolveLoginStatus(
  authClient: Object,
  processedCallback: boolean,
  maxRetries: number = 3
): Promise<boolean> {
  if (processedCallback) {
    await waitMs(500);
  }

  let isAlreadyLoggedIn = await authClient.isLoggedIn();

  if (!isAlreadyLoggedIn && processedCallback) {
    for (let retryCount = 0; retryCount < maxRetries && !isAlreadyLoggedIn; retryCount += 1) {
      // eslint-disable-next-line no-await-in-loop
      await waitMs(300);
      // eslint-disable-next-line no-await-in-loop
      isAlreadyLoggedIn = await authClient.isLoggedIn();
    }
  }

  if (processedCallback && !isAlreadyLoggedIn) {
    const idToken = await authClient.getIdToken();
    console.log('idToken structure:', idToken);
    if (getIdTokenEmailFromClaims(idToken)) {
      console.log('Found token after callback, using it');
      isAlreadyLoggedIn = true;
    } else {
      console.log('No token found after callback');
    }
  }

  return isAlreadyLoggedIn;
}

/**
 * Handles the authenticated branch after auth initialization succeeds.
 * Sets the email, refreshes feature/profile state, and schedules refreshes.
 */
export async function handleLoggedInUser({
  authClient,
  currentHasCode,
  hasLoginBeenAttempted,
  markLoginAttempted,
  setIsLoggedIn,
  setEmail,
  userProfileUpdateTime,
  fetchFeatureFlag,
  scheduleSessionAlignedRefresh
}: {
  authClient: Object,
  currentHasCode: boolean,
  hasLoginBeenAttempted: () => boolean,
  markLoginAttempted: () => void,
  setIsLoggedIn: (boolean) => void,
  setEmail: (string) => void,
  userProfileUpdateTime: () => void,
  fetchFeatureFlag: () => void,
  scheduleSessionAlignedRefresh: () => void
}): Promise<void> {
  const idToken = await authClient.getIdToken();
  logIdTokenStructure(idToken);

  const emailValue = await resolveEmailFromIdToken(
    () => authClient.getIdToken(),
    idToken
  );

  if (emailValue) {
    console.log('[TWithAuthorization] emailValue:', emailValue);
    setIsLoggedIn(true);
    setEmail(emailValue);
    setTimeout(() => {
      userProfileUpdateTime();
      fetchFeatureFlag();
    }, 2000);
    scheduleSessionAlignedRefresh();
    return;
  }

  setIsLoggedIn(false);
  if (!currentHasCode && !hasLoginBeenAttempted()) {
    markLoginAttempted();
    authClient.login();
  }
}

/**
 * Handles the unauthenticated branch after auth initialization completes.
 */
export function handleUnauthenticatedAfterInit({
  currentHasCode,
  processedCallback,
  hasLoginBeenAttempted,
  markLoginAttempted,
  authClient,
  setIsLoggedIn
}: {
  currentHasCode: boolean,
  processedCallback: boolean,
  hasLoginBeenAttempted: () => boolean,
  markLoginAttempted: () => void,
  authClient: Object,
  setIsLoggedIn: (boolean) => void
}): void {
  if (!currentHasCode && !hasLoginBeenAttempted() && !processedCallback) {
    markLoginAttempted();
    authClient.login();
    return;
  }
  setIsLoggedIn(false);
}

/**
 * Runs the full authorization bootstrap for the current browser location.
 * Reads query params, initializes the auth client, and routes to login or
 * the authenticated app state.
 */
export async function processAuthorizationOnInit({
  authClient,
  validatePathNameFn,
  hasLoginBeenAttempted,
  markLoginAttempted,
  setIsLoggedIn,
  setEmail,
  userProfileUpdateTime,
  fetchFeatureFlag,
  scheduleSessionAlignedRefresh
}: {
  authClient: Object,
  validatePathNameFn: Function,
  hasLoginBeenAttempted: () => boolean,
  markLoginAttempted: () => void,
  setIsLoggedIn: (boolean) => void,
  setEmail: (string) => void,
  userProfileUpdateTime: () => void,
  fetchFeatureFlag: () => void,
  scheduleSessionAlignedRefresh: () => void
}): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const collabId = params.get(QUERY_PARAM_COLLAB_ID) || 'null';
  const pathName = window.location.pathname;
  const bookmarkId = params.get(QUERY_PARAM_SAVED_SEARCH_ID) || 'null';

  persistQuerySessionIds(pathName, collabId, bookmarkId, validatePathNameFn);

  if (window.location.search.includes('error')) {
    setIsLoggedIn(false);
    return;
  }

  const currentHasCode = window.location.search.includes('code=');

  try {
    const processedCallback = await authClient.initialize();
    const isAlreadyLoggedIn = await resolveLoginStatus(authClient, processedCallback);

    if (isAlreadyLoggedIn) {
      await handleLoggedInUser({
        authClient,
        currentHasCode,
        hasLoginBeenAttempted,
        markLoginAttempted,
        setIsLoggedIn,
        setEmail,
        userProfileUpdateTime,
        fetchFeatureFlag,
        scheduleSessionAlignedRefresh
      });
      return;
    }

    handleUnauthenticatedAfterInit({
      currentHasCode,
      processedCallback,
      hasLoginBeenAttempted,
      markLoginAttempted,
      authClient,
      setIsLoggedIn
    });
  } catch (error) {
    console.log('Error in initialize:', error);
    if (!currentHasCode && !hasLoginBeenAttempted()) {
      markLoginAttempted();
      authClient.login();
    } else {
      setIsLoggedIn(false);
    }
  }
}
